<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <!-- 헤더 -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">사용자 관리</h1>
        <p class="mt-2 text-gray-600">시스템 사용자를 관리하고 권한을 설정하세요</p>
      </div>
    </div>

    <!-- 검색 및 필터 -->
    <div class="bg-white rounded-lg shadow-sm border p-4 mb-6">
      <div class="flex flex-col sm:flex-row gap-4">
        <div class="flex-1 relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="이름, 이메일로 검색..."
            class="form-input pl-10 w-full"
            @keyup.enter="loadUsers"
          />
          <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        </div>
        <button
          @click="loadUsers"
          class="btn btn-primary btn-md px-6"
        >
          검색
        </button>
      </div>
    </div>

    <!-- 사용자 목록 -->
    <div class="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div v-if="loading" class="p-8 text-center">
        <div class="spinner w-8 h-8 mx-auto mb-4"></div>
        <p class="text-gray-600">사용자 목록을 불러오는 중...</p>
      </div>

      <div v-else-if="users.length === 0" class="p-12 text-center">
        <UsersIcon class="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-medium text-gray-900 mb-2">사용자가 없습니다</h3>
        <p class="text-gray-600">검색 조건을 변경해보세요.</p>
      </div>

      <div v-else>
        <!-- 테이블 헤더 -->
        <div class="bg-gray-50 border-b px-6 py-3">
          <div class="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
            <div class="col-span-3">사용자 정보</div>
            <div class="col-span-2">연락처</div>
            <div class="col-span-2">권한</div>
            <div class="col-span-2">가입일</div>
            <div class="col-span-3 text-center">관리</div>
          </div>
        </div>

        <!-- 사용자 목록 -->
        <div class="divide-y divide-gray-200">
          <div
            v-for="user in users"
            :key="user.id"
            class="px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <div class="grid grid-cols-12 gap-4 items-center">
              <!-- 사용자 정보 -->
              <div class="col-span-3">
                <div class="flex items-center">
                  <div class="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <span class="text-primary-600 font-medium text-sm">
                      {{ user.name.charAt(0).toUpperCase() }}
                    </span>
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">{{ user.name }}</p>
                    <p class="text-sm text-gray-500">{{ user.email }}</p>
                  </div>
                </div>
              </div>

              <!-- 연락처 -->
              <div class="col-span-2">
                <p class="text-sm text-gray-600">{{ user.phone }}</p>
              </div>

              <!-- 권한 -->
              <div class="col-span-2">
                <span
                  class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                  :class="{
                    'bg-red-100 text-red-800': user.role === 'ADMIN',
                    'bg-blue-100 text-blue-800': user.role === 'USER'
                  }"
                >
                  {{ user.role === 'ADMIN' ? '관리자' : '일반 사용자' }}
                </span>
              </div>

              <!-- 가입일 -->
              <div class="col-span-2">
                <p class="text-sm text-gray-600">{{ formatDate(user.createdAt) }}</p>
              </div>

              <!-- 관리 액션 -->
              <div class="col-span-3">
                <div class="flex justify-center gap-2">
                  <button
                    @click="showUserDetail(user)"
                    class="text-blue-600 hover:text-blue-700 p-1"
                    title="상세 보기"
                  >
                    <EyeIcon class="w-4 h-4" />
                  </button>
                  <button
                    @click="showRoleChangeModal(user)"
                    class="text-yellow-600 hover:text-yellow-700 p-1"
                    title="권한 변경"
                  >
                    <KeyIcon class="w-4 h-4" />
                  </button>
                  <button
                    @click="confirmDeleteUser(user)"
                    :disabled="user.role === 'ADMIN' && adminCount <= 1"
                    class="text-red-600 hover:text-red-700 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="사용자 삭제"
                  >
                    <TrashIcon class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 페이지네이션 -->
    <div v-if="totalPages > 1" class="mt-6 flex justify-center">
      <nav class="flex items-center space-x-2">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage <= 1"
          class="btn-pagination"
          :class="{ 'opacity-50 cursor-not-allowed': currentPage <= 1 }"
        >
          <ChevronLeftIcon class="w-5 h-5" />
        </button>
        
        <span
          v-for="page in visiblePages"
          :key="page"
          class="btn-pagination"
          :class="{
            'bg-primary-600 text-white': page === currentPage,
            'cursor-pointer hover:bg-gray-100': page !== currentPage
          }"
          @click="page !== currentPage && changePage(page)"
        >
          {{ page }}
        </span>
        
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage >= totalPages"
          class="btn-pagination"
          :class="{ 'opacity-50 cursor-not-allowed': currentPage >= totalPages }"
        >
          <ChevronRightIcon class="w-5 h-5" />
        </button>
      </nav>
    </div>

    <!-- 사용자 상세 모달 -->
    <div v-if="selectedUser" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">사용자 상세 정보</h3>
          <button @click="selectedUser = null" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <div class="space-y-4">
          <div class="text-center">
            <div class="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span class="text-primary-600 font-bold text-2xl">
                {{ selectedUser.name.charAt(0).toUpperCase() }}
              </span>
            </div>
            <h4 class="text-xl font-medium">{{ selectedUser.name }}</h4>
            <p class="text-gray-600">{{ selectedUser.email }}</p>
          </div>
          
          <div class="border-t pt-4 space-y-3">
            <div class="flex justify-between">
              <span class="text-gray-600">전화번호:</span>
              <span>{{ selectedUser.phone }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">권한:</span>
              <span
                class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                :class="{
                  'bg-red-100 text-red-800': selectedUser.role === 'ADMIN',
                  'bg-blue-100 text-blue-800': selectedUser.role === 'USER'
                }"
              >
                {{ selectedUser.role === 'ADMIN' ? '관리자' : '일반 사용자' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">가입일:</span>
              <span>{{ formatDate(selectedUser.createdAt) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">최종 수정:</span>
              <span>{{ formatDate(selectedUser.updatedAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 권한 변경 모달 -->
    <div v-if="roleChangeUser" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">권한 변경</h3>
          <button @click="roleChangeUser = null" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <div class="space-y-4">
          <p class="text-gray-600">
            <strong>{{ roleChangeUser.name }}</strong>님의 권한을 변경하시겠습니까?
          </p>
          
          <div class="space-y-2">
            <label class="flex items-center">
              <input
                v-model="newRole"
                type="radio"
                value="USER"
                class="form-radio"
              />
              <span class="ml-2">일반 사용자</span>
            </label>
            <label class="flex items-center">
              <input
                v-model="newRole"
                type="radio"
                value="ADMIN"
                class="form-radio"
              />
              <span class="ml-2">관리자</span>
            </label>
          </div>
          
          <div class="flex gap-3 pt-4">
            <button
              @click="roleChangeUser = null"
              class="btn btn-outline btn-sm flex-1"
            >
              취소
            </button>
            <button
              @click="changeUserRole"
              :disabled="isChangingRole || newRole === roleChangeUser.role"
              class="btn btn-primary btn-sm flex-1"
              :class="{ 'opacity-50 cursor-not-allowed': isChangingRole || newRole === roleChangeUser.role }"
            >
              <span v-if="isChangingRole">변경 중...</span>
              <span v-else>변경하기</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  MagnifyingGlassIcon,
  UsersIcon,
  EyeIcon,
  KeyIcon,
  TrashIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/vue/24/outline'
import { adminApi } from '@/services/api'
import type { User } from '@/types'
import { useToast } from 'vue-toastification'

const toast = useToast()

const loading = ref(false)
const users = ref<User[]>([])
const searchQuery = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalElements = ref(0)
const totalPages = ref(0)
const adminCount = ref(0)

const selectedUser = ref<User | null>(null)
const roleChangeUser = ref<User | null>(null)
const newRole = ref<'USER' | 'ADMIN'>('USER')
const isChangingRole = ref(false)

const visiblePages = computed(() => {
  const pages = []
  const start = Math.max(1, currentPage.value - 2)
  const end = Math.min(totalPages.value, currentPage.value + 2)
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ko-KR')
}

const loadUsers = async () => {
  try {
    loading.value = true
    const response = await adminApi.getUsers({
      keyword: searchQuery.value || undefined,
      page: currentPage.value - 1,
      size: pageSize.value
    })
    
    users.value = response.content
    totalElements.value = response.totalElements
    totalPages.value = response.totalPages
    
    // 관리자 수 계산
    adminCount.value = users.value.filter(user => user.role === 'ADMIN').length
  } catch (error: any) {
    console.error('Failed to load users:', error)
    toast.error('사용자 목록을 불러오는데 실패했습니다.')
  } finally {
    loading.value = false
  }
}

const changePage = (page: number) => {
  currentPage.value = page
  loadUsers()
}

const showUserDetail = (user: User) => {
  selectedUser.value = user
}

const showRoleChangeModal = (user: User) => {
  roleChangeUser.value = user
  newRole.value = user.role
}

const changeUserRole = async () => {
  if (!roleChangeUser.value || newRole.value === roleChangeUser.value.role) return

  try {
    isChangingRole.value = true
    await adminApi.changeUserRole(roleChangeUser.value.id, { role: newRole.value })
    
    // 사용자 목록 업데이트
    const userIndex = users.value.findIndex(u => u.id === roleChangeUser.value!.id)
    if (userIndex !== -1) {
      users.value[userIndex].role = newRole.value
    }
    
    toast.success('사용자 권한이 변경되었습니다.')
    roleChangeUser.value = null
  } catch (error: any) {
    console.error('Failed to change user role:', error)
    toast.error('권한 변경에 실패했습니다.')
  } finally {
    isChangingRole.value = false
  }
}

const confirmDeleteUser = (user: User) => {
  if (user.role === 'ADMIN' && adminCount.value <= 1) {
    toast.error('마지막 관리자 계정은 삭제할 수 없습니다.')
    return
  }

  if (confirm(`정말 ${user.name}님의 계정을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
    deleteUser(user.id)
  }
}

const deleteUser = async (userId: number) => {
  try {
    await adminApi.deleteUser(userId)
    toast.success('사용자가 삭제되었습니다.')
    loadUsers()
  } catch (error: any) {
    console.error('Failed to delete user:', error)
    toast.error('사용자 삭제에 실패했습니다.')
  }
}

onMounted(() => {
  loadUsers()
})
</script>

<style scoped>
.btn-pagination {
  @apply px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors;
}

.btn-pagination:first-child {
  @apply rounded-l-md;
}

.btn-pagination:last-child {
  @apply rounded-r-md;
}

.btn-pagination:not(:first-child):not(:last-child) {
  @apply -ml-px;
}

.spinner {
  border: 2px solid #f3f4f6;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
